import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  TextField,
  Alert,
  IconButton,
} from '@mui/material';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import CustomButton from '../../MUI_custom_component/Button/CustomButton';
import { mapStyles } from '../mapStyles';
import {
  convertAddressToCoordinates,
  editAddressAction,
  saveAddressAction,
} from '../APIs/action';
import {
  formatCompleteAddress,
  validatePhoneNumber,
} from '../../../utils/settings';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

class GoogleMapsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Error caught by boundary
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='body1'
            color='text.secondary'
          >
            Map temporarily unavailable
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            Please refresh the page
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const AddressConfirmationModal = ({
  addressData = {},
  coordinates = {},
  onConfirm,
  onBack,
  isAddressBeingEdited = false,
  companyEsiID={companyEsiID}
}) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [map, setMap] = useState(null);
  const [error, setError] = useState({});
  const [alternateNumber, setAlternateNumber] = useState(
    addressData.phone_no || null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState({
    lat: null,
    lng: null,
  });

  useEffect(() => {
    if (coordinates && coordinates.lat && coordinates.lng) {
      setMapCoordinates({
        lat: Number(coordinates.lat),
        lng: Number(coordinates.lng),
      });
    } else {
      // If no coordinates provided, convert address to coordinates
      const formattedAddress = formatCompleteAddress(addressData);
      if (formattedAddress) {
        getCoordinatesFromAddress(formattedAddress);
      }
    }
  }, []);

  useEffect(() => {}, [mapCoordinates]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const {
    address_line_1 = '',
    address_line_2 = '',
    district_name = '',
    pincode_id = '',
    state_name = '',
  } = addressData;

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      map
    ) {
      try {
        setMap(null);
      } catch (error) {
        setMap(null);
      }
    } else {
      setMap(null);
    }
  }, []);

  const handleConfirm = async () => {
    setIsLoading(true);
    if (alternateNumber && !validatePhoneNumber(alternateNumber)) {
      setIsLoading(false);
      return toast.error(
        'Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9',
      );
    }

    try {
      let payload = {
        address_line_1: addressData.address_line_1 || '',
        address_line_2: addressData.address_line_2 || '',
        address_line_3: '',
        landmark: addressData.landmark || '',
        pincode: addressData.pincode_id || '',
        phone_no: alternateNumber,
      };
      let response = {};
      if (isAddressBeingEdited) {
        response = await editAddressAction(addressData.id, payload,companyEsiID);
      } else {
        payload = {
          ...payload,
          company_esi_id: companyEsiID,
        }
        response = await saveAddressAction(payload);
      }

      if (response.error) {
        setIsLoading(false);
        return;
      } else {
        toast.success(response.message);
        onConfirm({
          ...addressData,
          coordinates: coordinates,
          alternateNumber: alternateNumber,
        });
      }
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onBack();
  };

  const getCoordinatesFromAddress = async (address) => {
    if (!address || address.trim().length === 0) {
      return;
    }

    try {
      const response = await convertAddressToCoordinates(address);
      const { data, error } = response;
      if (error) {
        console.error('Error converting address to coordinates:', error);
        return null;
      }

      if (data && data.latitude && data.longitude) {
        const newCoordinates = {
          lat: Number(data.latitude),
          lng: Number(data.longitude),
        };

        setMapCoordinates(newCoordinates);
      }
    } catch (e) {
      console.error('Error:', e);
      return null;
    }
  };
  return (
    <Box
      sx={{
        height: isMobile ? '100vh' : '589px',
        width: isMobile ? '100%' : '618px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMobile ? 0 : '12px',
        position: 'relative',
      }}
    >
      
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: '200px',
        }}
      >
        {loadError ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Alert
              severity='error'
              sx={{ maxWidth: '400px' }}
            >
              {loadError?.message || 'Failed to load Google Maps'}
            </Alert>
          </Box>
        ) : !isLoaded ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Typography>Loading map...</Typography>
          </Box>
        ) : (
          <GoogleMapsErrorBoundary>
            <GoogleMap
              key='address-confirmation-map'
              mapContainerStyle={{
                height: '100%',
                width: '100%',
                borderTopLeftRadius: isMobile ? 0 : '12px',
                borderTopRightRadius: isMobile ? 0 : '12px',
              }}
              center={{ lat: mapCoordinates.lat, lng: mapCoordinates.lng }}
              zoom={15}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                styles: mapStyles,
                disableDefaultUI: true,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                gestureHandling: 'none',
              }}
            >
              {mapCoordinates && mapCoordinates.lat && mapCoordinates.lng && (
                <Marker
                  position={{
                    lat: mapCoordinates.lat,
                    lng: mapCoordinates.lng,
                  }}
                  draggable={false}
                  icon={
                    window.google && window.google.maps
                      ? {
                          url:
                            'data:image/svg+xml;charset=UTF-8,' +
                            encodeURIComponent(`
                                        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                                          <circle cx="60" cy="60" r="50" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                                          <path d="M60 40c-5.52 0-10 4.48-10 10 0 7.5 10 20 10 20s10-12.5 10-20c0-5.52-4.48-10-10-10zm0 13.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="#333333"/>
                                        </svg>
                                      `),
                          scaledSize: new window.google.maps.Size(120, 120),
                          anchor: new window.google.maps.Point(60, 75),
                        }
                      : undefined
                  }
                />
              )}
            </GoogleMap>
          </GoogleMapsErrorBoundary>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: 'white',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',

          overflowY: 'auto',
          borderBottomLeftRadius: isMobile ? 0 : '12px',
          borderBottomRightRadius: isMobile ? 0 : '12px',
          height: '272px',
          marginTop: '-20px',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Box sx={{}}>
          <Typography
            variant='h1'
            color='#333333'
          >
            Confirm your address
          </Typography>

          <Box sx={{ mt: '4px' }}>
            <Typography
              variant='p1'
              sx={{ color: '#333333' }}
            >
              {[address_line_1, address_line_2].filter(Boolean).join(', ')}
            </Typography>
            <Typography
              variant='p1'
              sx={{ color: '#333333' }}
            >
              {[district_name, state_name, pincode_id]
                .filter(Boolean)
                .join(', ')}
            </Typography>
          </Box>

          <Box sx={{ mt: '16px' }}>
            <Typography
              variant='labelD'
              sx={{ color: '#666666' }}
            >
              Whatsapp Number
            </Typography>
            <TextField
              variant='standard'
              fullWidth
              helperText={''}
              placeholder='Type Whatsapp number here'
              inputProps={{
                maxLength: 10,
                inputMode: 'numeric',
              }}
              value={alternateNumber}
              onChange={(e) => setAlternateNumber(e.target.value)}
              sx={{
                mt: '4px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  '& input': {
                    padding: '8px',
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: '32px' }}>
          <CustomButton
            variant='outlined'
            color='secondary'
            label='No, Change Address'
            onClick={handleBack}
            disabled={isLoading}
            sx={{
              flex: 1,
              width: '171px',
              height: '40px',
              borderWidth: '1px',
              borderColor: '#995A00',
              color: '#995A00',
              '&:hover': {
                borderColor: '#7a4600',
                backgroundColor: 'rgba(153, 90, 0, 0.04)',
              },
            }}
          />
          <CustomButton
            variant='filled'
            color='warning'
            label={isLoading ? 'Saving...' : 'Yes'}
            onClick={handleConfirm}
            disabled={isLoading}
            sx={{
              flex: 1,
              width: '171px',
              height: '40px',
              backgroundColor: '#995A00',
              '&:hover': {
                backgroundColor: '#7a4600',
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddressConfirmationModal;
